import {
  validateWithSchema,
  type FieldNameToClear,
  type FormFieldsErrorReader,
  type ValidateWithSchemaResult,
} from '@/utils/validation/zodToAntdFieldErrors';

import type { ContractValidationScope } from '@/utils/validation/contractValidation';
import type { FormSchema } from '@/utils/validation/formSchemaKit';
import type { ValidationScope } from '@/utils/validation/jobProgressFields';
import type { FormInstance } from 'antd/es/form';
import type { ZodType } from 'zod';

type ValidateForm = Pick<FormInstance, 'setFields' | 'scrollToField'> & {
  setFieldsValue?: FormInstance['setFieldsValue'];
  getFieldInstance?: FormInstance['getFieldInstance'];
  getFieldsError?: FormFieldsErrorReader['getFieldsError'];
};

export type ValidateSchemaFormOptions = {
  validationScope?: ValidationScope;
  contractValidationScope?: ContractValidationScope;
  fieldNamesToClear?: FieldNameToClear[];
  formName?: string;
};

/** Inject explicit null for absent required keys so Zod superRefine fires. */
export function normalizeSchemaFormValues(
  formSchema: FormSchema,
  values: Record<string, unknown>,
  validationScope: ValidationScope = 'save',
  contractValidationScope?: ContractValidationScope,
): Record<string, unknown> {
  const normalizedValues: Record<string, unknown> = { ...values };
  for (const key of formSchema.fieldNames) {
    if (
      !(key in normalizedValues) &&
      formSchema.isFieldRequired(
        key,
        normalizedValues,
        validationScope,
        contractValidationScope,
      )
    ) {
      // requiredString uses optional unions; undefined is treated as absent/valid.
      normalizedValues[key] = null;
    }
  }
  return normalizedValues;
}

export function parseSchemaFormValues(
  formSchema: FormSchema,
  values: Record<string, unknown>,
  validationScope: ValidationScope = 'save',
  contractValidationScope?: ContractValidationScope,
): { success: true; data: Record<string, unknown> } | { success: false } {
  const normalizedValues = normalizeSchemaFormValues(
    formSchema,
    values,
    validationScope,
    contractValidationScope,
  );
  const result = formSchema.zodSchema.safeParse(normalizedValues);
  if (!result.success) {
    return { success: false };
  }
  return { success: true, data: result.data as Record<string, unknown> };
}

export function validateSchemaForm(
  form: ValidateForm,
  formSchema: FormSchema,
  values: Record<string, unknown>,
  options: ValidateSchemaFormOptions = {},
): ValidateWithSchemaResult {
  const {
    validationScope = 'save',
    contractValidationScope,
    fieldNamesToClear,
    formName,
  } = options;

  const normalizedValues = normalizeSchemaFormValues(
    formSchema,
    values,
    validationScope,
    contractValidationScope,
  );

  return validateWithSchema<Record<string, unknown>>(
    form,
    formSchema.zodSchema as ZodType<Record<string, unknown>>,
    normalizedValues,
    fieldNamesToClear ?? Object.keys(normalizedValues),
    formName,
  );
}
